import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Paper,
  Grid,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
  MapPin,
  Clock,
  Car,
  DollarSign,
  Navigation
} from 'lucide-react';

interface Location {
  latitude: number;
  longitude: number;
}

interface TripData {
  id: number;
  otp: string | null;
  tip: number | null;
  fare: string;
  source: Location;
  status: string;
  userId: number;
  distance: string;
  driverId: number | null;
  dropFare: string;
  duration: number;
  created_at: string;
  pickupFare: string;
  updated_at: string;
  destination: Location;
  vehicleType: string;
  fare_changed: boolean;
  original_fare: string | null;
  ride_end_time: string | null;
  pickupDistance: string;
  source_address: string;
  destination_address: string;
}

interface MatchedDriverData {
    distance?: number;
    driverId?: number;
    duration?: number;
  }
  
  interface RideLog {
    id: string;
    tripId: number;
    event: string;
    data: {
      data?: MatchedDriverData[] | MatchedDriverData | null;
      trip: TripData;
    };
    timestamp: string;
  }

interface RideLogsModalProps {
  open: boolean;
  onClose: () => void;
  logs: RideLog[];
}

const RideLogsModal: React.FC<RideLogsModalProps> = ({ open, onClose, logs }) => {

      // Helper function to normalize driver data
  const normalizeDriverData = (data?: MatchedDriverData[] | MatchedDriverData | null): MatchedDriverData[] => {
    if (!data) return [];
    
    // If it's an array, return as-is
    if (Array.isArray(data)) {
      return data;
    }
    
    // If it's an object, wrap it in an array
    if (typeof data === 'object' && (data.driverId || data.distance !== undefined)) {
      return [data];
    }
    
    // Default to empty array
    return [];
  };


  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  const formatEvent = (event: string) => {
    return event.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getEventColor = (event: string) => {
    switch (event) {
      case 'ride_requested':
        return '#3b82f6'; // blue
      case 'driver_assigned':
        return '#10b981'; // green
      case 'ride_started':
        return '#6366f1'; // indigo
      case 'ride_completed':
        return '#059669'; // emerald
      case 'ride_cancelled':
        return '#ef4444'; // red
      default:
        return '#6b7280'; // gray
    }
  };

  const getEventIcon = (event: string) => {
    switch (event) {
      case 'ride_requested':
        return <MapPin className="w-6 h-6" />;
      case 'driver_assigned':
        return <Car className="w-6 h-6" />;
      case 'ride_started':
        return <Navigation className="w-6 h-6" />;
      case 'ride_completed':
        return <DollarSign className="w-6 h-6" />;
      default:
        return <Clock className="w-6 h-6" />;
    }
  };

  const renderMatchedDrivers = (data?: MatchedDriverData[] | MatchedDriverData | null) => {
    const drivers = normalizeDriverData(data);
    
    if (drivers.length === 0) {
      return (
        <Typography 
          variant="body2" 
          color="text.secondary" 
          sx={{ p: 2, textAlign: 'center' }}
        >
          No driver information available
        </Typography>
      );
    }

    return (
      <Box sx={{ mt: 2, p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2, pl: 1 }}>
          Matched Drivers
        </Typography>
        <Grid container spacing={2}>
          {drivers.map((driver, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Paper 
                variant="outlined" 
                sx={{ 
                  p: 2, 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 2,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: 2,
                    transform: 'scale(1.02)'
                  }
                }}
              >
                <Box 
                  sx={{ 
                    bgcolor: 'primary.light', 
                    p: 1, 
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Car className="w-6 h-6 text-primary" />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Driver ID: {driver.driverId ?? 'N/A'}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                  >
                    <MapPin size={16} />
                    Distance: {driver.distance !== undefined ? `${driver.distance.toFixed(2)} km` : 'N/A'}
                  </Typography>
                  {driver.duration !== undefined && (
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                    >
                      <Clock size={16} />
                      Duration: {driver.duration} min
                    </Typography>
                  )}
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '70vh' }
      }}
    >
      <DialogTitle sx={{ 
        m: 0, 
        p: 2, 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        borderBottom: '1px solid',
        borderColor: 'divider'
      }}>
        <Typography variant="h6">Ride Timeline</Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {logs.map((log, index) => (
            <Paper 
              key={log.id}
              elevation={1}
              sx={{ 
                p: 3,
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: '4px',
                  bgcolor: getEventColor(log.event),
                  borderRadius: '4px 0 0 4px'
                }
              }}
            >

                              {/* Event Header */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Box sx={{ 
                  p: 1, 
                  borderRadius: '50%', 
                  bgcolor: `${getEventColor(log.event)}15`,
                  color: getEventColor(log.event)
                }}>
                  {getEventIcon(log.event)}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" sx={{ color: getEventColor(log.event) }}>
                    {formatEvent(log.event)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatTimestamp(log.timestamp)}
                  </Typography>
                </Box>
              </Box>
              
                {renderMatchedDrivers(log.data.data)}



              {/* Trip Details */}
              {log.data.trip && (
                <Grid container spacing={3}>
                  {/* Status and Basic Info */}
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Status: <Typography component="span" color="text.primary">{log.data.trip.status}</Typography>
                      </Typography>
                      <Typography variant="subtitle2" color="text.secondary">
                        Vehicle: <Typography component="span" color="text.primary">{log.data.trip.vehicleType}</Typography>
                      </Typography>
                      <Typography variant="subtitle2" color="text.secondary">
                        Distance: <Typography component="span" color="text.primary">{log.data.trip.distance} km</Typography>
                      </Typography>
                      <Typography variant="subtitle2" color="text.secondary">
                        Duration: <Typography component="span" color="text.primary">{log.data.trip.duration} min</Typography>
                      </Typography>
                    </Box>
                  </Grid>

                  {/* Fare Details */}
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Total Fare: <Typography component="span" color="text.primary">₹{log.data.trip.fare}</Typography>
                      </Typography>
                      <Typography variant="subtitle2" color="text.secondary">
                        Pickup Fare: <Typography component="span" color="text.primary">₹{log.data.trip.pickupFare}</Typography>
                      </Typography>
                      <Typography variant="subtitle2" color="text.secondary">
                        Drop Fare: <Typography component="span" color="text.primary">₹{log.data.trip.dropFare}</Typography>
                      </Typography>
                    </Box>
                  </Grid>

                  {/* Location Details */}
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <MapPin size={16} /> Pickup: 
                        <Typography component="span" color="text.primary">{log.data.trip.source_address}</Typography>
                      </Typography>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <MapPin size={16} /> Drop: 
                        <Typography component="span" color="text.primary">{log.data.trip.destination_address}</Typography>
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              )}
            </Paper>
          ))}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default RideLogsModal;